'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/ui/components/Dialog'
import { Icon } from '@/ui/atoms/Icon'
import { trackEvent } from '@/lib/analytics/posthog'

const BULLETS = [
  'Tell me about your project',
  'Talk through the challenges you’re facing',
  'Just a conversation — no sales pitch',
]

// Veronika's Google appointment-scheduling link. utm appended best-effort
// (Google may strip it on redirect — the trackEvent below is the reliable
// attribution signal).
const CALENDAR_URL = 'https://calendar.app.google/kuekuBhrXjC5sZfi9'
const CALENDAR_HREF = `${CALENDAR_URL}?utm_source=exit_cta&utm_medium=popup`

// Once shown, don't show again for 7 days (per browser).
const STORAGE_KEY = 'ea_exit_cta_last_shown_v1'
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000

// Mobile timer fallback + a short arm delay so we don't fire the instant the
// page loads / the cursor darts to the tab bar.
const MOBILE_TIMER_MS = 40_000
const ARM_DELAY_MS = 4_000

type PageType = 'vendor_detail' | 'product_detail' | null

/**
 * Returns which gated page-type the current path is, or null if the exit CTA
 * should not arm here. Strips the locale prefix and matches exact depths so
 * listing pages (e.g. /companies, /products-solutions) are
 * excluded.
 */
function getPageType(pathname: string): PageType {
  const segs = pathname.split('/').filter(Boolean)
  // [locale, 'platform', 'companies', slug]
  if (segs[1] === 'platform' && segs[2] === 'companies' && segs.length === 4) {
    return 'vendor_detail'
  }
  // [locale, 'platform', 'products', groupSlug, productSlug]
  if (segs[1] === 'platform' && segs[2] === 'products' && segs.length === 5) {
    return 'product_detail'
  }
  return null
}

function withinCooldown(): boolean {
  try {
    const last = window.localStorage.getItem(STORAGE_KEY)
    if (!last) return false
    const ts = Number(last)
    if (!Number.isFinite(ts)) return false
    return Date.now() - ts < COOLDOWN_MS
  } catch {
    return false
  }
}

export function ExitIntentCTA() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const pageTypeRef = useRef<PageType>(null)

  const fire = useCallback((trigger: string) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()))
    } catch {
      // localStorage unavailable (private mode / blocked) — still show once.
    }
    setOpen(true)
    trackEvent('exit_cta_shown', {
      trigger,
      page_type: pageTypeRef.current,
      path: window.location.pathname,
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const pageType = getPageType(pathname)
    pageTypeRef.current = pageType

    // Manual preview: append ?previewExitCta=1 to any URL to force the popup
    // open instantly, ignoring page-type gating and the 7-day cap. Non-
    // destructive — it does not write the cap, so real behaviour is unaffected.
    if (new URLSearchParams(window.location.search).has('previewExitCta')) {
      setOpen(true)
      return
    }

    if (!pageType) return
    if (withinCooldown()) return

    let fired = false
    const trigger = (name: string) => {
      if (fired) return
      fired = true
      cleanup()
      fire(name)
    }

    // --- Desktop: mouse leaves out the top of the viewport ---
    const onMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger('exit_intent')
    }

    // --- Mobile: fast scroll back up after scrolling down, or a timer ---
    let lastY = window.scrollY
    let hasScrolledDown = false
    const onScroll = () => {
      const y = window.scrollY
      if (y > 500) hasScrolledDown = true
      if (hasScrolledDown && y < lastY - 8 && y < 300) trigger('scroll_up')
      lastY = y
    }

    const isTouch = window.matchMedia('(pointer: coarse)').matches

    let mobileTimerId: ReturnType<typeof setTimeout> | undefined

    const arm = () => {
      if (isTouch) {
        window.addEventListener('scroll', onScroll, { passive: true })
        mobileTimerId = setTimeout(() => trigger('timer'), MOBILE_TIMER_MS)
      } else {
        document.addEventListener('mouseout', onMouseOut)
      }
    }

    function cleanup() {
      if (timerId) clearTimeout(timerId)
      if (mobileTimerId) clearTimeout(mobileTimerId)
      window.removeEventListener('scroll', onScroll)
      document.removeEventListener('mouseout', onMouseOut)
    }

    const timerId = setTimeout(arm, ARM_DELAY_MS)

    return cleanup
  }, [pathname, fire])

  const handleSchedule = () => {
    trackEvent('exit_cta_schedule_clicked', {
      page_type: pageTypeRef.current,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    })
    // Let the anchor's default navigation (new tab) proceed.
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && open) {
      trackEvent('exit_cta_dismissed', { page_type: pageTypeRef.current })
    }
    setOpen(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="p-0 overflow-hidden rounded-lg border border-border-subtle w-[calc(100vw-2rem)] sm:w-[440px] sm:max-w-[440px]"
      >
        {/* Hero band — founder identity for trust */}
        <div className="relative bg-soft-gradient-blue px-6 pt-6 pb-5">
          <div className="flex items-center gap-4 pe-8">
            <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/40 backdrop-blur">
              <span className="text-body-M-semibold text-white">VW</span>
            </div>
            <div>
              <p className="text-body-M-semibold text-white">Dr. Veronika Wright</p>
              <p className="text-label-XS-regular text-white/85">
                Founder &amp; CEO · Electrification Academy
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-6">
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-blue-97 px-2.5 py-1 text-label-XS-semibold text-blue-20">
            <Icon name="chat-3-line" className="!size-3.5 text-blue-20" />
            A quick 15-min chat
          </span>

          <DialogTitle className="!mb-2 !text-headline-S-semibold !text-text-base">
            I&apos;d love to hear what you&apos;re working on
          </DialogTitle>
          <DialogDescription className="mb-5 !text-body-S-regular !text-text-subtle">
            I started Electrification Academy to understand the real challenges
            people face in EV &amp; battery work. I&apos;d genuinely like to hear about
            your project and what you&apos;re trying to solve — no pitch, just a
            conversation.
          </DialogDescription>

          <ul className="mb-6 space-y-2.5">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-soft-gradient-blue">
                  <Icon name="ri-check-line" className="!size-3 text-white" />
                </span>
                <span className="text-body-S-regular text-text-base">{b}</span>
              </li>
            ))}
          </ul>

          <Button
            variant="primary"
            size="lg"
            asChild
            className="w-full shadow-sm transition-shadow hover:shadow-md"
          >
            <a href={CALENDAR_HREF} target="_blank" rel="noopener noreferrer" onClick={handleSchedule}>
              <ButtonContent showIconLeft iconLeft="ri-calendar-event-fill" label="Find a time to talk" />
            </a>
          </Button>

          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="mt-3 w-full text-center text-label-XS-regular text-text-subtle transition-colors hover:text-text-base"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
